import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { ServerState } from 'pages/catalog/[[...slug]]'
import { RefinementsPanelItem } from './refinements-panel-item'
import { ExpandablePanel } from './expandable-panel'
import { environment } from '@config/environment'
import { furnitureRefinements, flooringRefinements } from '@services/algolia'
import { Panels, Refinement, SiteDomain } from 'types'
import { useSessionStorage } from '@curran-catalog/curran-atomic-library'

const getRefinementPanelId = (refinement: Refinement) => {
  return refinement.options?.attribute
}

const getPanelId = (refinement: Refinement) => {
  const panelId = []
  panelId.push(getRefinementPanelId(refinement))
  return panelId.join(':')
}

const getRefinementPanelAttribute = (refinement: Refinement) => {
  return refinement.options?.attribute
}

const getPanelAttributes = (refinement: Refinement) => {
  const panelAttributes = []
  panelAttributes.push(getRefinementPanelAttribute(refinement))
  return panelAttributes
}

export type PanelProps = {
  children: ReactNode
  panelId: string
  header?: React.ReactNode | string
  attributes?: unknown[]
  isOpened?: boolean
  [index: string]: unknown
  onToggle: (panelId: string) => void
}

export const PanelComponent = ({
  children,
  onToggle,
  panelId,
  attributes,
  serverFilterFacets,
  ...props
}: PanelProps & Pick<ServerState, 'serverFilterFacets'>) => {
  const onToggleMemoized = useCallback(() => onToggle(panelId), [onToggle, panelId])

  return (
    <ExpandablePanel
      onToggle={onToggleMemoized}
      serverFilterFacets={serverFilterFacets}
      attributes={attributes as string[]}
      {...props}
    >
      {children}
    </ExpandablePanel>
  )
}

const isFurniture = environment.siteDomain === SiteDomain.FURNITURE
const isFlooring = environment.siteDomain === SiteDomain.FLOORING

const refinements = isFurniture ? furnitureRefinements : flooringRefinements

const getStoredFilterPanelsKey = () => {
  if (isFurniture) {
    return 'furnitureFilterPanels'
  } else if (isFlooring) {
    return 'flooringFilterPanels'
  } else {
    return 'sisalcarpetFilterPanels'
  }
}

export const RefinementsPanel = ({ serverFilterFacets }: Pick<ServerState, 'serverFilterFacets'>) => {
  const [storedValue, setValue] = useSessionStorage(getStoredFilterPanelsKey(), {})
  const [panels, setPanels] = useState<Panels>({})

  useEffect(() => {
    if (!storedValue) {
      setPanels((prevPanels) => {
        const newPanelsState = {
          ...prevPanels,
          ...refinements.reduce((acc, current) => {
            return {
              ...acc,
              [getPanelId(current)]: Boolean(current.isExpanded),
            }
          }, {}),
        }

        return newPanelsState
      })
    } else setPanels(storedValue)
  }, [storedValue])

  const onToggle = useCallback(
    (panelId: string) => {
      setPanels((prevPanels) => {
        const newPanelsState = {
          ...prevPanels,
          [panelId]: !prevPanels[panelId],
        }
        setValue({ ...newPanelsState })
        return newPanelsState
      })
    },

    [setValue],
  )

  const PanelItem = useMemo(
    () =>
      refinements.map((refinement, index) => {
        return (
          <RefinementsPanelItem
            key={`${refinement.type}-${index}`}
            type={refinement.type}
            hidden={refinement.hidden}
            {...refinement.options}
          />
        )
      }),
    [],
  )

  const Panels = useMemo(
    () =>
      PanelItem.map((widget, i) => {
        const refinement = refinements[i]
        const panelId = getPanelId(refinement)
        const panelAttributes = getPanelAttributes(refinement)

        return (
          <PanelComponent
            key={panelId}
            panelId={panelId}
            attributes={panelAttributes}
            header={refinement.header}
            hidden={refinement.hidden}
            isOpened={panels[panelId]}
            serverFilterFacets={serverFilterFacets}
            onToggle={onToggle}
          >
            {widget}
          </PanelComponent>
        )
      }),
    [PanelItem, onToggle, panels, serverFilterFacets],
  )

  return <>{Panels}</>
}
